import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import React, { useMemo } from "react";
import useCountries, { Country } from "stores/useStore/useCountries";

const FlagIcon = styled("img")(({ theme }) => ({
  marginLeft: theme.spacing(1),
  marginRight: theme.spacing(1),
}));

type Props = {
  multiple?: boolean;
  defaultValue?: any;
  value?: any;
  onChange: (event: any, newValue: string | null) => void;
};

export default function CountrySelect({
  multiple = false,
  defaultValue,
  value: outSideValue,
  onChange,
}: Props) {
  const { data: countries } = useCountries();

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option: Country) => option.country + "|" + option.isoNumeric,
  });

  defaultValue = useMemo(() => {
    if (defaultValue) {
      return defaultValue;
    } else if (countries) {
      const us = countries.find(
        (item) => item.isoNumeric.toLocaleUpperCase() === "US"
      );
      if (multiple) {
        return [us];
      } else {
        return us;
      }
    }
  }, [countries]);

  const [value, setValue] = React.useState(defaultValue);

  React.useEffect(() => {
    if (outSideValue) {
      setValue(outSideValue);
    }
  }, [outSideValue]);

  const handleChange = (ev, country) => {
    setValue(country);
    onChange(ev, country);
  };

  const isOptionEqualToValue = (option: any, value: any) => {
    return option.id === value.id;
  };

  const autoCompeleteProps = React.useMemo(() => {
    if (value) {
      return { value, onChange: handleChange };
    }
    return {};
  }, [outSideValue, value]);

  if (!countries) {
    return <div></div>;
  }

  return (
    <Autocomplete
      // {...autoCompeleteProps}
      value={value}
      onChange={handleChange}
      multiple={multiple}
      options={countries}
      defaultValue={defaultValue}
      isOptionEqualToValue={isOptionEqualToValue}
      autoHighlight
      disableClearable
      getOptionLabel={(option) => option.country}
      fullWidth
      filterOptions={filterOptions}
      popupIcon={<KeyboardArrowDownIcon />}
      renderTags={() => {
        return <div />;
      }}
      renderOption={(props, option, { selected }) => (
        <Box
          component="li"
          sx={{
            px: 1.5,
            fontsize: (theme) => theme.typography.body2.fontSize,
            "& > img": { mr: 2, flexShrink: 0 },
          }}
          {...props}
        >
          {multiple && (
            <Checkbox
              // icon={icon}
              // checkedIcon={checkedIcon}
              sx={{ ml: -2, mr: 0.5 }}
              checked={selected}
            />
          )}
          <img
            loading="lazy"
            width="20"
            src={`https://flagcdn.com/w20/${option.isoNumeric.toLowerCase()}.png`}
            srcSet={`https://flagcdn.com/w40/${option.isoNumeric.toLowerCase()}.png 2x`}
            alt=""
          />
          {option.country} ( {option.isoNumeric} )
        </Box>
      )}
      renderInput={(params) => {
        const { InputProps, inputProps, ...otherParams } = params;
        if (!multiple) {
          InputProps.startAdornment = (
            <FlagIcon
              loading="lazy"
              width="32"
              src={`https://flagcdn.com/w20/${value?.isoNumeric.toLowerCase()}.png`}
              srcSet={`https://flagcdn.com/w40/${value?.isoNumeric.toLowerCase()}.png 2x`}
              alt=""
            />
          );
        }
        return (
          <TextField
            {...otherParams}
            InputProps={InputProps}
            fullWidth
            placeholder="Search countries"
            inputProps={{
              ...inputProps,
              autoComplete: "new-password", // disable autocomplete and autofill
            }}
          />
        );
      }}
    />
  );
}
